import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { HttpAgent, Message } from "@ag-ui/client";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { apiClient, apiMutate } from "~/utils/apiClient";

// MCP servers whose apps render in the sidebar's Apps section instead of
// inline in the message stream. `useMcpAppEvents` skips these so a given app
// never renders in both places.
export const PANE_MCP_SERVER_IDS: ReadonlySet<string> = new Set(["cohort_explorer"]);

// Label for the sidebar's Apps section, one row per server rather than per
// app instance — all open cohorts group under a single "Cohorts" row.
export const MCP_APP_GROUP_LABELS: Readonly<Record<string, string>> = {
  cohort_explorer: "Cohorts",
};

// Per-server configuration for the pane's server-agnostic mechanics: how to
// recognize a server's state object among a tool result's possible shapes, how
// to derive a stable identity for it (so repeated edits update one tab), and
// how to refresh it on history rehydration. Adding a new pane-hosted server is
// a new entry here — no changes needed to the tab/coercion logic below.
interface McpAppMatch {
  itemId: string;
  title?: string;
}

interface McpAppConfig {
  match(obj: Record<string, unknown>): McpAppMatch | null;
  // Recognizes a "this instance was deleted" result (e.g. delete_cohort's
  // response) and returns its itemId so the pane can close/remove the tab,
  // rather than opening a tab for it.
  matchRemoved?(obj: Record<string, unknown>): string | null;
  refresh?: {
    tool: string;
    buildArgs(itemId: string): Record<string, unknown>;
  };
}

const MCP_APP_CONFIGS: Readonly<Record<string, McpAppConfig>> = {
  cohort_explorer: {
    match: (obj) =>
      typeof obj.id === "number" && Array.isArray(obj.criteria)
        ? { itemId: String(obj.id), title: typeof obj.name === "string" ? obj.name : undefined }
        : null,
    matchRemoved: (obj) =>
      obj.deleted === true && typeof obj.cohort_id === "number" ? String(obj.cohort_id) : null,
    refresh: {
      tool: "get_cohort",
      buildArgs: (itemId) => ({ cohort_id: Number(itemId) }),
    },
  },
};

// Parses a tool result the same way `coerceState` would unwrap it (JSON string
// or already-parsed object) and checks it against a server's `matchRemoved`
// predicate. Deletion payloads are flat and not MCP-content-block-wrapped, so
// unlike `coerceState` this doesn't need to recurse into arrays/nested objects.
function matchRemovedId(raw: unknown, serverId: string): string | null {
  const config = MCP_APP_CONFIGS[serverId];
  if (!config?.matchRemoved) return null;
  let parsed: unknown = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return config.matchRemoved(parsed as Record<string, unknown>);
}

const CE_MCP_APP_ENABLED = import.meta.env.VITE_CE_MCP_APP_ENABLED === "true";
const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface McpAppRegistryEntry {
  tool_call_name: string;
  server_id: string;
  resource_uri: string;
  title: string;
}

export interface McpAppTab {
  // Stable identity of an app instance. Two results from the same app instance
  // (e.g. edits to the same cohort) share a key and update one tab in place.
  key: string;
  serverId: string;
  resourceUri: string;
  title: string;
  // undefined = not fetched yet, null = fetch failed, string = loaded.
  html: string | null | undefined;
  // The latest tool result, pushed into the iframe as its render state.
  state: unknown;
  // True while the tab's HTML is loading or its live state is being refreshed.
  loading: boolean;
}

export interface McpAppsPaneState {
  tabs: McpAppTab[]; // open, in insertion order
  hidden: McpAppTab[]; // closed but restorable
  activeKey: string | null;
  setActive: (key: string) => void;
  close: (key: string) => void;
  restore: (key: string) => void;
  // Apply state returned by a panel-initiated tool call to the tab in place.
  applyLocalUpdate: (key: string, rawState: unknown) => void;
}

interface PaneInternal {
  byKey: Map<string, McpAppTab>;
  openOrder: string[];
  hiddenOrder: string[];
  activeKey: string | null;
}

const EMPTY_INTERNAL: PaneInternal = {
  byKey: new Map(),
  openOrder: [],
  hiddenOrder: [],
  activeKey: null,
};

interface McpAppsPaneContextValue {
  pane: PaneInternal;
  setPane: Dispatch<SetStateAction<PaneInternal>>;
  refreshing: ReadonlySet<string>;
  setRefreshing: Dispatch<SetStateAction<ReadonlySet<string>>>;
}

// Holds the pane's state above both `Chat` (which owns the agent/messages that
// drive it) and `SessionContextSidebar` (which renders it) — the two are
// siblings in the tree, so the state has to live in their shared ancestor,
// `ProjectLayout`.
const McpAppsPaneContext = createContext<McpAppsPaneContextValue | null>(null);

export const McpAppsPaneProvider = ({ children }: { children: React.ReactNode }) => {
  const [pane, setPane] = useState<PaneInternal>(EMPTY_INTERNAL);
  const [refreshing, setRefreshing] = useState<ReadonlySet<string>>(() => new Set());

  return (
    <McpAppsPaneContext.Provider value={{ pane, setPane, refreshing, setRefreshing }}>
      {children}
    </McpAppsPaneContext.Provider>
  );
};

function useMcpAppsPaneContext(): McpAppsPaneContextValue {
  const ctx = useContext(McpAppsPaneContext);
  if (!ctx) {
    throw new Error("useMcpAppsPaneContext must be used within a McpAppsPaneProvider");
  }
  return ctx;
}

// Recursively dig out the server-shaped object from a tool result, which may
// arrive as a JSON string, an MCP content-block array, or a nested object.
function coerceState(raw: unknown, serverId: string, depth = 0): Record<string, unknown> | null {
  if (depth > 6 || raw == null) return null;
  if (typeof raw === "string") {
    try {
      return coerceState(JSON.parse(raw), serverId, depth + 1);
    } catch {
      return null;
    }
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (
        item &&
        typeof item === "object" &&
        typeof (item as { text?: unknown }).text === "string"
      ) {
        const fromText = coerceState((item as { text: string }).text, serverId, depth + 1);
        if (fromText) return fromText;
      }
      const got = coerceState(item, serverId, depth + 1);
      if (got) return got;
    }
    return null;
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (MCP_APP_CONFIGS[serverId]?.match(obj)) return obj;
    for (const value of Object.values(obj)) {
      const got = coerceState(value, serverId, depth + 1);
      if (got) return got;
    }
  }
  return null;
}

/**
 * Subscribes to MCP-app UI resources destined for the sidebar's Apps section,
 * one tab per app instance. Unlike `useMcpAppEvents` (inline, per-tool-call,
 * reset each run), this accumulates across runs and keys tabs by app instance
 * so repeated edits update a single tab in place. Called from `Chat`, where
 * the agent/messages driving it live; the tab state itself is held in
 * `McpAppsPaneContext` so `SessionContextSidebar` — a sibling of `Chat` in the
 * tree, not a descendant — can read and render it via `useMcpAppsPaneState`.
 */
export function useMcpAppsPaneSync(agent: HttpAgent, messages: ReadonlyArray<Message>): void {
  const { setPane, setRefreshing } = useMcpAppsPaneContext();
  // A live tool call is a composer-triggered action, so it should bring the
  // sidebar to the Apps section to show its result — history rehydration
  // (below) is a passive restore and shouldn't steal focus the same way.
  const { openPanel } = useSessionContextPanel();
  const htmlCache = useRef<Map<string, string | null>>(new Map());
  const inflight = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  // Guards the one-shot history rehydration per conversation.
  const hydratedRef = useRef(false);

  const markRefreshing = useCallback((key: string, on: boolean) => {
    setRefreshing((prev) => {
      if (on === prev.has(key)) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // Patches the fetched (or failed) html into any tab still awaiting this
  // resource. Shared by both the success and failure paths of `ensureHtml` so
  // a failure surfaces in state instead of leaving tabs stuck loading forever.
  const patchHtml = useCallback(
    (resourceUri: string, html: string | null) => {
      setPane((prev) => {
        const byKey = new Map(prev.byKey);
        let changed = false;
        for (const [key, tab] of byKey) {
          if (tab.resourceUri === resourceUri && tab.html === undefined) {
            byKey.set(key, { ...tab, html });
            changed = true;
          }
        }
        return changed ? { ...prev, byKey } : prev;
      });
    },
    [setPane]
  );

  // Fetch a resource's HTML once (cached) and patch it into any tab awaiting it.
  const ensureHtml = useCallback(
    (resourceUri: string, serverId: string) => {
      if (htmlCache.current.has(resourceUri) || inflight.current.has(resourceUri)) return;
      inflight.current.add(resourceUri);
      const params = new URLSearchParams({ server_id: serverId, uri: resourceUri });
      const url = `${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`;
      apiClient<string>(url, { expectsText: true, signal: abortRef.current?.signal })
        .then((html) => {
          htmlCache.current.set(resourceUri, html);
          patchHtml(resourceUri, html);
        })
        .catch(() => {
          htmlCache.current.set(resourceUri, null);
          patchHtml(resourceUri, null);
        })
        .finally(() => inflight.current.delete(resourceUri));
    },
    [patchHtml]
  );

  // Insert or update a tab, keyed by app instance (e.g. a cohort), so results
  // from any source — live events, panel edits, history hydration — converge
  // on one tab. Updating an existing instance leaves its open/hidden placement
  // alone. Returns the tab's key so callers can react to a specific instance
  // (e.g. bringing the sidebar to the Apps section for a live event).
  const upsertTab = useCallback(
    (serverId: string, resourceUri: string, state: unknown, fallbackTitle: string) => {
      const matched = MCP_APP_CONFIGS[serverId]?.match(state as Record<string, unknown>);
      const key = `${serverId}:${matched?.itemId ?? resourceUri}`;
      const title = matched?.title || fallbackTitle;
      setPane((prev) => {
        const existing = prev.byKey.get(key);
        const byKey = new Map(prev.byKey);
        byKey.set(key, {
          key,
          serverId,
          resourceUri,
          title,
          html: existing?.html ?? htmlCache.current.get(resourceUri),
          state,
          loading: existing?.loading ?? false,
        });
        if (existing) return { ...prev, byKey };
        // A brand-new instance opens as a tab and becomes active.
        return {
          byKey,
          openOrder: [...prev.openOrder, key],
          hiddenOrder: prev.hiddenOrder,
          activeKey: key,
        };
      });
      ensureHtml(resourceUri, serverId);
      return key;
    },
    [ensureHtml, setPane]
  );

  // Fully removes a tab (open or hidden) — used when the underlying item was
  // deleted, so unlike `close` there's no restoring it from `hidden` afterward.
  const removeTab = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.byKey.has(key)) return prev;
        const byKey = new Map(prev.byKey);
        byKey.delete(key);
        const openOrder = prev.openOrder.filter((k) => k !== key);
        const hiddenOrder = prev.hiddenOrder.filter((k) => k !== key);
        const activeKey =
          prev.activeKey === key ? (openOrder[openOrder.length - 1] ?? null) : prev.activeKey;
        return { byKey, openOrder, hiddenOrder, activeKey };
      });
    },
    [setPane]
  );

  // Live: subscribe to `mcp_app_ui` events for pane servers during a run.
  useEffect(() => {
    if (!CE_MCP_APP_ENABLED) return;

    setPane(EMPTY_INTERNAL);
    setRefreshing(new Set());
    htmlCache.current = new Map();
    inflight.current = new Set();
    hydratedRef.current = false;
    abortRef.current = new AbortController();

    const sub = agent.subscribe({
      onCustomEvent: ({ event }: { event: unknown }) => {
        const e = event as { name?: string; value?: Record<string, unknown> };
        if (e.name !== "mcp_app_ui") return;

        const v = e.value;
        if (!v?.tool_call_id || !v.resource_uri || !v.mcp_server_id) return;
        const serverId = v.mcp_server_id as string;
        if (!PANE_MCP_SERVER_IDS.has(serverId)) return;

        const resourceUri = v.resource_uri as string;

        const removedId = matchRemovedId(v.tool_result, serverId);
        if (removedId != null) {
          removeTab(`${serverId}:${removedId}`);
          return;
        }

        // Only a recognized-shape result opens/updates a tab; a non-renderable
        // result (e.g. an error from a rejected write) is ignored so it can't
        // spawn an empty panel.
        const state = coerceState(v.tool_result, serverId);
        if (!state) return;

        upsertTab(serverId, resourceUri, state, (v.title as string) || "MCP App");
        // The group (e.g. "Cohorts") is what the sidebar shows a row for, not
        // a specific instance — panel.id is the serverId, not the tab key.
        openPanel(SessionContextPanelType.APPS, serverId);
      },
    });

    return () => {
      sub.unsubscribe();
      abortRef.current?.abort();
    };
  }, [agent, upsertTab, removeTab, openPanel, setPane, setRefreshing]);

  // Rehydrate on page refresh: once history loads, rebuild a tab per app
  // instance from persisted tool results (newest snapshot wins), then fold in
  // the freshest server state via each server's configured refresh tool so
  // panel edits made after the last agent turn survive too. If the server was
  // restarted (item gone) the refresh 404s and the history snapshot stands.
  useEffect(() => {
    if (!CE_MCP_APP_ENABLED || hydratedRef.current || messages.length === 0) return;
    hydratedRef.current = true;

    let cancelled = false;
    const signal = abortRef.current?.signal;

    (async () => {
      let registry: { entries: McpAppRegistryEntry[] };
      try {
        registry = await apiClient<{ entries: McpAppRegistryEntry[] }>(
          `${VITE_ANALYSIS_AGENT_API}/mcp-app-registry`,
          { signal }
        );
      } catch {
        // Transient failure (e.g. the analysis-agent wasn't ready yet) — reset the guard so
        // the next messages update (more history streaming in) gives hydration another shot,
        // instead of silently giving up for the rest of the mount's lifetime.
        hydratedRef.current = false;
        return;
      }
      if (cancelled) return;

      const entryByTool = new Map(
        registry.entries
          .filter((e) => PANE_MCP_SERVER_IDS.has(e.server_id))
          .map((e) => [e.tool_call_name, e] as const)
      );
      if (entryByTool.size === 0) return;

      const resultByCallId = new Map<string, string>();
      for (const m of messages) {
        const tm = m as { role?: string; toolCallId?: string; content?: string };
        if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
          resultByCallId.set(tm.toolCallId, tm.content);
        }
      }

      const seeds = new Map<
        string,
        { entry: McpAppRegistryEntry; state: Record<string, unknown> }
      >();
      for (const m of messages) {
        const calls = (m as { toolCalls?: { id: string; function: { name: string } }[] }).toolCalls;
        if (!calls) continue;
        for (const tc of calls) {
          const entry = entryByTool.get(tc.function.name);
          if (!entry) continue;
          const raw = resultByCallId.get(tc.id);
          if (raw === undefined) continue;
          const state = coerceState(raw, entry.server_id);
          if (!state) continue;
          const matched = MCP_APP_CONFIGS[entry.server_id]?.match(state);
          const key = `${entry.server_id}:${matched?.itemId ?? entry.resource_uri}`;
          seeds.set(key, { entry, state }); // later message in order wins
        }
      }
      if (cancelled || seeds.size === 0) return;

      for (const { entry, state } of seeds.values()) {
        upsertTab(entry.server_id, entry.resource_uri, state, entry.title);
      }

      await Promise.all(
        [...seeds.entries()].map(async ([key, { entry, state }]) => {
          const config = MCP_APP_CONFIGS[entry.server_id];
          const matched = config?.match(state);
          if (!config?.refresh || !matched) return;
          markRefreshing(key, true);
          try {
            const fresh = await apiMutate<unknown>(
              `${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`,
              "POST",
              {
                server_id: entry.server_id,
                tool: config.refresh.tool,
                arguments: config.refresh.buildArgs(matched.itemId),
              },
              { signal }
            );
            if (cancelled) return;
            const coerced = coerceState(fresh, entry.server_id);
            if (!coerced) return;
            upsertTab(entry.server_id, entry.resource_uri, coerced, entry.title);
          } catch {
            // Server restarted or item gone: keep the history snapshot.
          } finally {
            markRefreshing(key, false);
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, upsertTab, markRefreshing]);
}

/**
 * Read-only projection of the pane's tabs plus the actions to switch/close/
 * restore them — what `SessionContextSidebar`'s Apps section and its detail
 * view render from. Backed by the same `McpAppsPaneContext` that
 * `useMcpAppsPaneSync` writes into.
 */
export function useMcpAppsPaneState(): McpAppsPaneState {
  const { pane, setPane, refreshing } = useMcpAppsPaneContext();

  const setActive = useCallback(
    (key: string) => {
      setPane((prev) => (prev.openOrder.includes(key) ? { ...prev, activeKey: key } : prev));
    },
    [setPane]
  );

  const close = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.openOrder.includes(key)) return prev;
        const openOrder = prev.openOrder.filter((k) => k !== key);
        const activeKey =
          prev.activeKey === key ? (openOrder[openOrder.length - 1] ?? null) : prev.activeKey;
        return {
          ...prev,
          openOrder,
          hiddenOrder: [...prev.hiddenOrder, key],
          activeKey,
        };
      });
    },
    [setPane]
  );

  const restore = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.hiddenOrder.includes(key)) return prev;
        return {
          ...prev,
          hiddenOrder: prev.hiddenOrder.filter((k) => k !== key),
          openOrder: [...prev.openOrder, key],
          activeKey: key,
        };
      });
    },
    [setPane]
  );

  const applyLocalUpdate = useCallback(
    (key: string, rawState: unknown) => {
      setPane((prev) => {
        const existing = prev.byKey.get(key);
        if (!existing) return prev;
        // Not every panel-initiated tool call returns the server's full state — e.g.
        // cohort_explorer's get_plotly_visualizations/create_access_request/
        // create_child_dataset all return something else entirely. Only sync the tab
        // when the result actually matches this server's state shape; otherwise leave
        // the tab's state untouched.
        const state = coerceState(rawState, existing.serverId);
        if (!state) return prev;
        const matched = MCP_APP_CONFIGS[existing.serverId]?.match(state as Record<string, unknown>);
        const title = matched?.title || existing.title;
        const byKey = new Map(prev.byKey);
        byKey.set(key, { ...existing, title, state });
        return { ...prev, byKey };
      });
    },
    [setPane]
  );

  return useMemo(() => {
    const withLoading = (t: McpAppTab): McpAppTab => ({
      ...t,
      loading: refreshing.has(t.key) || t.html === undefined,
    });
    const tabs = pane.openOrder
      .map((k) => pane.byKey.get(k))
      .filter((t): t is McpAppTab => t != null)
      .map(withLoading);
    const hidden = pane.hiddenOrder
      .map((k) => pane.byKey.get(k))
      .filter((t): t is McpAppTab => t != null)
      .map(withLoading);
    return {
      tabs,
      hidden,
      activeKey: pane.activeKey,
      setActive,
      close,
      restore,
      applyLocalUpdate,
    };
  }, [pane, refreshing, setActive, close, restore, applyLocalUpdate]);
}
